import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  Paper,
  Divider,
  Stack,
  useMediaQuery,
  IconButton,
  Tooltip
} from '@mui/material';
import { format } from 'date-fns';
import { JobWorkWithDetails } from './types';
import ImageIcon from '@mui/icons-material/Image';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

interface JobWorkHeaderProps {
  jobWork: JobWorkWithDetails;
  onPrintChallan: () => void;
  onAddReceipt: () => void;
  onMarkComplete?: () => void;
  onProcessJob?: () => void;
  onIssueJob?: () => void;
  onAddInvoice?: () => void;
  onEdit?: () => void;
  loading: boolean;
}

const JobWorkHeader: React.FC<JobWorkHeaderProps> = ({
  jobWork,
  onPrintChallan,
  onAddReceipt,
  onMarkComplete,
  onProcessJob,
  onIssueJob,
  onAddInvoice,
  onEdit,
  loading
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'IN_PROGRESS':
        return 'info';
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'CUTTING':
        return 'primary';
      case 'STITCHING':
        return 'secondary';
      case 'WASHING':
        return 'info';
      case 'FINISHING':
        return 'success';
      default:
        return 'default';
    }
  };

  const isMdUp = useMediaQuery('(min-width:900px)');

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={2}>
          <Box
            sx={{
              width: '100%',
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              mb: 2
            }}
          >
            {jobWork.design?.image_url ? (
              <Box
                component="img"
                src={jobWork.design.image_url}
                alt={`Design ${jobWork.designNo}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            ) : (
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <ImageIcon sx={{ fontSize: 40 }} />
                <Typography variant="caption" display="block">
                  No image
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="h5" component="h1" sx={{ mr: 2 }}>
              {jobWork.jobWorkCode}
            </Typography>
            <Chip 
              label={jobWork.status} 
              color={getStatusColor(jobWork.status) as any} 
              size="small" 
              sx={{ mr: 1 }}
            />
            <Chip 
              label={jobWork.jobType?.name || 'Unknown'} 
              color={getTypeColor(jobWork.jobType?.name || '') as any} 
              size="small" 
              sx={{ mr: 1 }}
            />
            {jobWork.processMaterial ? (
              <Chip 
                label="Process Material" 
                color="info" 
                size="small"
                sx={{ 
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  fontWeight: 'bold',
                  border: '1px solid #90caf9'
                }}
              />
            ) : (
              <Chip 
                label="Design Details" 
                color="secondary" 
                size="small"
                sx={{ 
                  backgroundColor: '#f3e5f5',
                  color: '#7b1fa2',
                  fontWeight: 'bold',
                  border: '1px solid #ce93d8'
                }}
              />
            )}
            <Tooltip title="Print Challan">
              <IconButton size="small" onClick={onPrintChallan} sx={{ ml: 1 }} aria-label="print challan">
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="body1" color="text.secondary">
              Design: {jobWork.designNo}
            </Typography>
            {onEdit && (
              <Button 
                variant="outlined" 
                color="primary" 
                size="small" 
                startIcon={<EditIcon />}
                onClick={onEdit}
              >
                Edit
              </Button>
            )}
          </Box>
          
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Vendor: {jobWork.vendor?.registered_name || jobWork.vendor?.alias || 'Unknown'}
          </Typography>
          
          <Box display="flex" gap={2} mt={2}>
            <Typography variant="body2" color="text.secondary">
              Issue Date: {jobWork.issueDate ? format(new Date(jobWork.issueDate), 'dd/MM/yyyy') : 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expected Return: {jobWork.expectedReturn ? format(new Date(jobWork.expectedReturn), 'dd/MM/yyyy') : 'N/A'}
            </Typography>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ rowGap: 1, columnGap: 1 }}>
              {/* Primary CTAs */}
              {jobWork.status === 'PENDING' && (
                <Button variant="contained" color="primary" onClick={onIssueJob} disabled={loading} size={isMdUp ? 'medium' : 'small'}>
                  {isMdUp ? 'Issue Job' : 'Issue'}
                </Button>
              )}
              {jobWork.vendor?.is_internal && (jobWork.status === 'ISSUED' || jobWork.status === 'PARTIALLY_RECEIVED') && (
                <Button variant="contained" color="info" onClick={onProcessJob} disabled={loading} startIcon={isMdUp ? <PlayCircleIcon /> : undefined} size={isMdUp ? 'medium' : 'small'}>
                  {isMdUp ? 'Process Job' : 'Process'}
                </Button>
              )}
              {jobWork.status !== 'COMPLETED' && (
                <Button variant="contained" color="secondary" onClick={onAddReceipt} disabled={loading} startIcon={isMdUp ? <AddIcon /> : undefined} size={isMdUp ? 'medium' : 'small'}>
                  {isMdUp ? 'Add Receipt' : 'Receipt'}
                </Button>
              )}
              {jobWork.status !== 'CANCELLED' && !jobWork.invoiceNumber && jobWork.receivedItems && jobWork.receivedItems.length > 0 && onAddInvoice && (
                <Button variant="contained" color="warning" onClick={onAddInvoice} disabled={loading} startIcon={isMdUp ? <ReceiptLongIcon /> : undefined} size={isMdUp ? 'medium' : 'small'}>
                  {isMdUp ? 'Add Invoice' : 'Invoice'}
                </Button>
              )}
            </Stack>
          </Box>
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 2 }} />
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Total Quantity
          </Typography>
          <Typography variant="h6">
            {jobWork.processMaterial
              ? jobWork.materials.reduce((sum, material) => sum + (material.quantity || 0), 0) + ' ' + (jobWork.materials[0]?.uom || 'PCS')
              : jobWork.designDetails.reduce((sum, detail) => sum + (detail.pieces || 0), 0) + ' PCS'
            }
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Processing Rate
          </Typography>
          <Typography variant="h6">
            {jobWork.processMaterial
              ? (jobWork.materials.length > 0
                ? '₹' + (jobWork.materials.reduce((sum, material) => sum + (material.rate || 0), 0) / jobWork.materials.length).toFixed(2) + ' / ' + (jobWork.materials[0]?.uom || 'PCS')
                : '₹0 / PCS')
              : (jobWork.designDetails.length > 0 
                ? '₹' + (jobWork.designDetails.reduce((sum, detail) => sum + (detail.rate || 0), 0) / jobWork.designDetails.length).toFixed(2) + ' / PCS' 
                : '₹0 / PCS')
            }
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Total Amount
              </Typography>
              <Typography variant="h6">
                {jobWork.processMaterial
                  ? '₹' + jobWork.materials.reduce((sum, material) => sum + ((material.quantity || 0) * (material.rate || 0)), 0).toFixed(2)
                  : '₹' + jobWork.designDetails.reduce((sum, detail) => sum + ((detail.pieces || 0) * (detail.rate || 0)), 0).toFixed(2)
                }
              </Typography>
            </Box>
            {jobWork.status !== 'COMPLETED' && (
              <Button
                variant="contained"
                color="success"
                onClick={onMarkComplete}
                disabled={loading}
                startIcon={<CheckCircleIcon />}
                size={isMdUp ? 'medium' : 'small'}
              >
                {isMdUp ? 'Mark as Complete' : 'Complete'}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default JobWorkHeader; 